import { useState, useEffect, useCallback } from "react";
import { appointmentsAPI, patientsAPI } from "@/lib/api";
import { validate, appointmentSchema } from "@/lib/validations";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Clock,
  User,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { format, addDays, subDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, startOfMonth, endOfMonth, getDay, addMonths, subMonths } from "date-fns";
import { es } from "date-fns/locale";

const STATUS_COLORS = {
  scheduled: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  confirmed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  completed: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  cancelled: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
  no_show: "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
};

const STATUS_LABELS = {
  scheduled: "Programada",
  confirmed: "Confirmada",
  completed: "Completada",
  cancelled: "Cancelada",
  no_show: "No asistio",
};

const TIME_SLOTS = [];
for (let h = 7; h <= 20; h++) {
  TIME_SLOTS.push(`${String(h).padStart(2, "0")}:00`);
  TIME_SLOTS.push(`${String(h).padStart(2, "0")}:30`);
}

const EMPTY_APPT = {
  patient_id: "",
  date: "",
  start_time: "09:00",
  end_time: "10:00",
  notes: "",
  status: "scheduled",
};

export default function AgendaPage() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_APPT);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("day");

  const dateStr = format(selectedDate, "yyyy-MM-dd");

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      let params = {};
      if (view === "day") {
        params.date = dateStr;
      } else if (view === "week") {
        const wStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const wEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
        params.date_from = format(wStart, "yyyy-MM-dd");
        params.date_to = format(wEnd, "yyyy-MM-dd");
      } else if (view === "month") {
        const mStart = startOfMonth(selectedDate);
        const mEnd = endOfMonth(selectedDate);
        params.date_from = format(mStart, "yyyy-MM-dd");
        params.date_to = format(mEnd, "yyyy-MM-dd");
      }
      const res = await appointmentsAPI.list(params);
      setAppointments(res.data.appointments || []);
    } catch {
      toast.error("Error cargando citas");
    } finally {
      setLoading(false);
    }
  }, [dateStr, view, selectedDate]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  useEffect(() => {
    patientsAPI.list({ limit: 200 }).then((r) => setPatients(r.data.patients || [])).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    const v = validate(appointmentSchema, form);
    if (!v.ok) {
      toast.error(Object.values(v.errors)[0]);
      return;
    }
    setSaving(true);
    try {
      await appointmentsAPI.create({ ...form, date: dateStr });
      toast.success("Cita creada");
      setShowNew(false);
      setForm(EMPTY_APPT);
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear cita");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await appointmentsAPI.update(id, { status: newStatus });
      toast.success("Estado actualizado");
      fetchAppointments();
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const todayAppointments = appointments.filter(
    (a) => a.date === dateStr
  ).sort((a, b) => a.start_time?.localeCompare(b.start_time));

  const weekDays = eachDayOfInterval({
    start: startOfWeek(selectedDate, { weekStartsOn: 1 }),
    end: endOfWeek(selectedDate, { weekStartsOn: 1 }),
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="agenda-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Agenda</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {format(selectedDate, "EEEE, d 'de' MMMM yyyy", { locale: es })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-muted rounded-lg p-0.5">
            {["day", "week", "month"].map((v) => (
              <button
                key={v}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  view === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setView(v)}
                data-testid={`view-${v}`}
              >
                {v === "day" ? "Dia" : v === "week" ? "Semana" : "Mes"}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            className="rounded-full"
            onClick={() => { setForm({ ...EMPTY_APPT }); setShowNew(true); }}
            data-testid="new-appointment-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Nueva cita
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar Sidebar */}
        <Card className="lg:col-span-1">
          <CardContent className="p-3">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              locale={es}
              className="rounded-md"
              data-testid="agenda-calendar"
            />
            <div className="mt-4 flex items-center justify-between px-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setSelectedDate(subDays(selectedDate, 1))}
                data-testid="prev-day-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => setSelectedDate(new Date())}
                data-testid="today-btn"
              >
                Hoy
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setSelectedDate(addDays(selectedDate, 1))}
                data-testid="next-day-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Appointments */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">
              {view === "day" ? "Citas del dia" : "Citas de la semana"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />)}
              </div>
            ) : view === "day" ? (
              todayAppointments.length > 0 ? (
                <div className="space-y-2">
                  {todayAppointments.map((appt) => (
                    <div
                      key={appt.id}
                      className="flex items-center gap-4 p-3.5 rounded-xl border border-border hover:border-primary/30 transition-colors group"
                      data-testid={`agenda-appt-${appt.id}`}
                    >
                      <div className="w-20 flex-shrink-0 text-center">
                        <p className="text-sm font-semibold text-foreground">{appt.start_time}</p>
                        <p className="text-[10px] text-muted-foreground">{appt.end_time}</p>
                      </div>
                      <div className="w-px h-10 bg-border" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{appt.patient_name}</p>
                        {appt.notes && <p className="text-xs text-muted-foreground truncate mt-0.5">{appt.notes}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={appt.status}
                          onValueChange={(val) => handleStatusChange(appt.id, val)}
                        >
                          <SelectTrigger className="h-7 w-28 text-[10px]" data-testid={`status-select-${appt.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_LABELS).map(([k, v]) => (
                              <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-medium">No hay citas para este dia</p>
                  <Button variant="link" size="sm" className="mt-1" onClick={() => setShowNew(true)} data-testid="empty-new-appt-btn">
                    Agendar una cita
                  </Button>
                </div>
              )
            ) : view === "month" ? (
              (() => {
                const mStart = startOfMonth(selectedDate);
                const mEnd = endOfMonth(selectedDate);
                const mDays = eachDayOfInterval({ start: mStart, end: mEnd });
                const padStart = getDay(mStart) === 0 ? 6 : getDay(mStart) - 1;
                return (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Button variant="ghost" size="sm" onClick={() => setSelectedDate(subMonths(selectedDate, 1))} data-testid="prev-month-btn">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm font-semibold capitalize">
                        {format(selectedDate, "MMMM yyyy", { locale: es })}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedDate(addMonths(selectedDate, 1))} data-testid="next-month-btn">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
                      {["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"].map((d) => (
                        <div key={d} className="p-2 text-center text-[10px] font-semibold text-muted-foreground bg-muted uppercase">{d}</div>
                      ))}
                      {Array(padStart).fill(null).map((_, i) => (
                        <div key={`p-${i}`} className="p-2 bg-card min-h-[56px]" />
                      ))}
                      {mDays.map((day) => {
                        const ds = format(day, "yyyy-MM-dd");
                        const dayAppts = appointments.filter((a) => a.date === ds);
                        const isToday = isSameDay(day, new Date());
                        return (
                          <div
                            key={ds}
                            className={`p-2 bg-card min-h-[56px] cursor-pointer hover:bg-muted/50 transition-colors ${isToday ? "ring-1 ring-inset ring-primary" : ""}`}
                            onClick={() => { setSelectedDate(day); setView("day"); }}
                            data-testid={`month-day-${ds}`}
                          >
                            <span className={`text-xs font-medium ${isToday ? "text-primary font-bold" : "text-foreground"}`}>
                              {format(day, "d")}
                            </span>
                            {dayAppts.length > 0 && (
                              <div className="mt-1 space-y-0.5">
                                {dayAppts.slice(0, 2).map((a) => (
                                  <div key={a.id} className="text-[9px] truncate px-1 py-0.5 rounded bg-primary/10 text-primary font-medium">
                                    {a.start_time} {a.patient_name?.split(" ")[0]}
                                  </div>
                                ))}
                                {dayAppts.length > 2 && (
                                  <span className="text-[9px] text-muted-foreground">+{dayAppts.length - 2} mas</span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
            ) : (
              /* Week View */
              <div className="space-y-4">
                {weekDays.map((day) => {
                  const dayStr = format(day, "yyyy-MM-dd");
                  const dayAppts = appointments.filter((a) => a.date === dayStr).sort((a, b) => a.start_time?.localeCompare(b.start_time));
                  const isToday = isSameDay(day, new Date());
                  return (
                    <div key={dayStr}>
                      <div
                        className={`flex items-center gap-2 py-1.5 px-2 rounded-lg mb-1.5 cursor-pointer hover:bg-muted transition-colors ${
                          isToday ? "bg-primary/5" : ""
                        }`}
                        onClick={() => { setSelectedDate(day); setView("day"); }}
                      >
                        <span className={`text-xs font-semibold uppercase ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                          {format(day, "EEE d", { locale: es })}
                        </span>
                        {dayAppts.length > 0 && (
                          <Badge variant="secondary" className="text-[10px] h-5">{dayAppts.length} cita{dayAppts.length > 1 ? "s" : ""}</Badge>
                        )}
                      </div>
                      {dayAppts.length > 0 ? (
                        <div className="ml-4 space-y-1.5">
                          {dayAppts.map((a) => (
                            <div key={a.id} className="flex items-center gap-2 text-sm py-1">
                              <Clock className="w-3 h-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground w-12">{a.start_time}</span>
                              <span className="text-sm text-foreground">{a.patient_name}</span>
                              <Badge className={`ml-auto text-[9px] ${STATUS_COLORS[a.status]}`}>{STATUS_LABELS[a.status]}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="ml-4 text-xs text-muted-foreground/60">Sin citas</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New Appointment Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-md" data-testid="new-appointment-dialog">
          <DialogHeader>
            <DialogTitle>Nueva cita</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Paciente</Label>
              <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                <SelectTrigger data-testid="appt-patient-select">
                  <SelectValue placeholder="Seleccionar paciente" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <div className="flex items-center gap-2">
                        <User className="w-3 h-3" />
                        {p.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Hora inicio</Label>
                <Select value={form.start_time} onValueChange={(v) => setForm({ ...form, start_time: v })}>
                  <SelectTrigger data-testid="appt-start-time">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hora fin</Label>
                <Select value={form.end_time} onValueChange={(v) => setForm({ ...form, end_time: v })}>
                  <SelectTrigger data-testid="appt-end-time">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                placeholder="Notas opcionales..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                data-testid="appt-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-full" disabled={saving} data-testid="appt-save-btn">
                {saving ? "Guardando..." : "Crear cita"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
